import { useMutation } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import {
  Box,
  Button,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { StatusChip } from "components/StatusChip";
import {
  EpisodicContainerRemovalKind,
  EpisodicEventItemFragment,
  WasteContainerStatus,
  WasteEpisodicEventStatus,
} from "generated-graphql/graphql";
import pluralize from "pluralize";
import { ReactElement, useState } from "react";

import { formatContainerDate, formatReportablePounds } from "../Containers/containerDisplay";
import { WasteContainerDialog } from "../Containers/WasteContainerDialog";
import { WasteContainerDrawer } from "../Containers/WasteContainerDrawer";
import { WasteContainerStatusChip } from "../Containers/WasteContainerStatusChip";
import { SET_CONTAINER_EPISODIC_EVENT_MUTATION } from "./api";
import { AttachContainerDialog } from "./AttachContainerDialog";
import { describeAttachError } from "./episodicErrors";

type EventContainerRecord = EpisodicEventItemFragment["containerHistory"][number];
type RecordedContainer = EventContainerRecord["container"];

const REFETCH_QUERIES = ["FacilityEpisodicEvents", "FacilityAccumulationSummary"];

/** Default rows per page for each event's container list. */
const CONTAINERS_PAGE_SIZE = 5;

const DETACH_CONFIRM_COPY =
  "This container's current waste stops counting as the event's, including waste already recorded.";

function formatEventPounds(lb: number | null | undefined): string {
  if (lb == null) {
    return "—";
  }
  return lb === 0 ? "0 lb" : formatReportablePounds(lb);
}

const REMOVAL_STATUS: Record<EpisodicContainerRemovalKind, ReactElement> = {
  [EpisodicContainerRemovalKind.Shipped]: (
    <WasteContainerStatusChip status={WasteContainerStatus.Shipped} />
  ),
  [EpisodicContainerRemovalKind.Emptied]: (
    <StatusChip color="default" variant="soft" label="Emptied" />
  ),
  [EpisodicContainerRemovalKind.None]: <StatusChip color="default" variant="soft" label="Closed" />,
};

function RecordStatus({ record }: { record: EventContainerRecord }) {
  return record.isAttached ? (
    <WasteContainerStatusChip status={record.container.status} />
  ) : (
    REMOVAL_STATUS[record.lastRemoval]
  );
}

function RecordRow({
  record,
  canDetach,
  onView,
  onDetach,
  detaching,
}: {
  record: EventContainerRecord;
  canDetach: boolean;
  onView: (containerId: string) => void;
  onDetach: (container: RecordedContainer) => void;
  detaching: boolean;
}) {
  const { container } = record;
  return (
    <TableRow>
      <TableCell>
        <Link component="button" variant="body2" onClick={() => onView(container.id)}>
          {container.containerLabel}
        </Link>
      </TableCell>
      <TableCell>{container.wasteStream?.name ?? "—"}</TableCell>
      <TableCell>
        {container.accumulationArea
          ? `${container.accumulationArea.name} (${container.accumulationArea.type})`
          : "—"}
      </TableCell>
      <TableCell>{record.attachedAt ? formatContainerDate(record.attachedAt) : "—"}</TableCell>
      <TableCell>{formatEventPounds(record.addedLb)}</TableCell>
      <TableCell>{formatEventPounds(record.shippedLb)}</TableCell>
      <TableCell>
        <RecordStatus record={record} />
      </TableCell>
      <TableCell align="right">
        {canDetach && record.isAttached && (
          <Button size="small" disabled={detaching} onClick={() => onDetach(container)}>
            Detach
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}

function EventContainersTable({
  records,
  canDetach,
  onView,
  onDetach,
  detaching,
}: {
  records: EventContainerRecord[];
  canDetach: boolean;
  onView: (containerId: string) => void;
  onDetach: (container: RecordedContainer) => void;
  detaching: boolean;
}) {
  const [rawPage, setRawPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(CONTAINERS_PAGE_SIZE);
  const lastPage = Math.max(0, Math.ceil(records.length / rowsPerPage) - 1);
  const page = Math.min(rawPage, lastPage);
  const pagedRecords = records.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Container</TableCell>
            <TableCell>Waste stream</TableCell>
            <TableCell>Area</TableCell>
            <TableCell>Attached</TableCell>
            <TableCell>Added</TableCell>
            <TableCell>Shipped</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {pagedRecords.map((record) => (
            <RecordRow
              key={record.container.id}
              record={record}
              canDetach={canDetach}
              onView={onView}
              onDetach={onDetach}
              detaching={detaching}
            />
          ))}
        </TableBody>
      </Table>
      {records.length > CONTAINERS_PAGE_SIZE && (
        <TablePagination
          component="div"
          count={records.length}
          page={page}
          onPageChange={(_event, nextPage) => setRawPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(Number(e.target.value));
            setRawPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      )}
    </TableContainer>
  );
}

/**
 * The attach-mutation wiring shared by create-and-attach and detach.
 * Create-then-attach: the standard container dialog creates the container,
 * then it joins the event via the same attach mutation the picker uses. A
 * failed attach leaves a perfectly good container behind — the message says
 * so instead of pretending the whole thing failed.
 */
function useEventContainerMutations(eventId: string, onDetachSettled: () => void) {
  const alerts = useAlerts();
  const [setContainerEpisodicEvent, { loading: detaching }] = useMutation(
    SET_CONTAINER_EPISODIC_EVENT_MUTATION,
    { refetchQueries: REFETCH_QUERIES }
  );

  const handleCreated = async (containerId: string) => {
    await setContainerEpisodicEvent({
      variables: { containerId, episodicEventId: eventId },
      onCompleted: () => {
        alerts.success("Container created and attached to event");
      },
      onError: (error) => {
        alerts.error(
          `Container created, but it couldn't join the event: ${describeAttachError(
            error,
            "an error occurred while attaching it"
          )}`
        );
      },
    });
  };

  const handleDetach = async (containerId: string) => {
    await setContainerEpisodicEvent({
      variables: { containerId, episodicEventId: null, detachFromEpisodicEventId: eventId },
      onCompleted: () => {
        alerts.success("Container detached from event");
        onDetachSettled();
      },
      onError: (error) => {
        alerts.error(error.message || "An error occurred while detaching the container");
        onDetachSettled();
      },
    });
  };

  return { handleCreated, handleDetach, detaching };
}

export function EventContainersSection({ event }: { event: EpisodicEventItemFragment }) {
  const [attachOpen, setAttachOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [viewContainerId, setViewContainerId] = useState<string | null>(null);
  const [detachCandidate, setDetachCandidate] = useState<RecordedContainer | null>(null);
  const { handleCreated, handleDetach, detaching } = useEventContainerMutations(event.id, () =>
    setDetachCandidate(null)
  );

  const isOpen = event.status !== WasteEpisodicEventStatus.Finalized;

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
        sx={{ mb: 1 }}
      >
        <Typography variant="caption" color="text.secondary">
          {isOpen
            ? `${event.containers.length} attached now · clocking to the 60-day window`
            : `${pluralize("container", event.containerHistory.length, true)} on record`}
        </Typography>
        {isOpen && (
          <Stack direction="row" spacing={1}>
            <Button size="small" onClick={() => setAttachOpen(true)}>
              Attach existing containers
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Add />}
              onClick={() => setCreateOpen(true)}
            >
              New container
            </Button>
          </Stack>
        )}
      </Stack>

      {event.containerHistory.length > 0 && (
        <EventContainersTable
          records={event.containerHistory}
          canDetach={isOpen}
          onView={setViewContainerId}
          onDetach={setDetachCandidate}
          detaching={detaching}
        />
      )}

      {viewContainerId && (
        <WasteContainerDrawer
          containerId={viewContainerId}
          open
          onClose={() => setViewContainerId(null)}
        />
      )}

      {attachOpen && (
        <AttachContainerDialog
          eventId={event.id}
          open={attachOpen}
          onClose={() => setAttachOpen(false)}
        />
      )}

      {createOpen && (
        <WasteContainerDialog
          facilityId={event.facilityId}
          onClose={() => setCreateOpen(false)}
          onCreated={handleCreated}
          episodicEvent={{
            reason: event.reason,
            startDate: event.startDate,
            accumulationDeadline: event.accumulationDeadline,
          }}
        />
      )}

      <ConfirmDialog
        open={detachCandidate != null}
        onClose={() => setDetachCandidate(null)}
        onConfirm={() => {
          if (detachCandidate) {
            void handleDetach(detachCandidate.id);
          }
        }}
        loading={detaching}
        header={`Detach ${detachCandidate?.containerLabel ?? "container"}?`}
        info={DETACH_CONFIRM_COPY}
        confirmText="Detach"
      />
    </Box>
  );
}
